import { Stack, Title, Text, Divider, Paper, Group } from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const LabelText = (props) => <Text size="sm" fw={500} c="dimmed" {...props} />;

const Field = ({ label, value }) => (
  <Group>
    <LabelText>{label}:</LabelText>
    <Text>{value}</Text>
  </Group>
);

const RegistrationFields = ({
  formData,
  registrationCustomFields,
  formatCustomFieldValue,
  t,
}) => (
  <>
    <Field
      label={t("registration_type")}
      value={formData.type === "individual" ? t("individual") : t("company")}
    />

    {formData.type === "individual" ? (
      <Field
        label={t("name")}
        value={`${formData.firstName} ${formData.lastName}`}
      />
    ) : (
      <>
        <Field
          label={t("company_name")}
          value={formData.companyName || "N/A"}
        />
        {formData.vatNumber && (
          <Field label={t("vat_number")} value={formData.vatNumber} />
        )}
      </>
    )}

    <Field label={t("email")} value={formData.email} />

    {registrationCustomFields.length > 0 && formData.additionalFields && (
      <>
        <Divider my="xs" />
        <Text size="sm" fw={600}>
          {t("additional_information")}
        </Text>
        {registrationCustomFields.map((field) => (
          <Field
            key={field.key}
            label={field.label}
            value={formatCustomFieldValue(
              field,
              formData.additionalFields[field.key]
            )}
          />
        ))}
      </>
    )}
  </>
);

const TicketCard = ({
  ticket,
  index,
  ticketCustomFields,
  getTicketTypeName,
  formatCustomFieldValue,
  t,
}) => (
  <Paper p="sm" withBorder style={{ backgroundColor: "#f8f9fa" }}>
    <Title order={6} mb="xs">
      {t("attendee")} #{index + 1}
    </Title>

    <Stack gap="xs">
      <Field
        label={t("name")}
        value={`${ticket.firstName} ${ticket.lastName}`}
      />
      <Field label={t("email")} value={ticket.email} />

      {ticket.ticketTypeId && (
        <Field
          label={t("ticket_type")}
          value={getTicketTypeName(ticket.ticketTypeId)}
        />
      )}

      {ticketCustomFields.length > 0 && ticket.additionalFields && (
        <>
          <Divider my="xs" />
          <Text size="sm" fw={600}>
            {t("additional_information")}
          </Text>
          {ticketCustomFields.map((field) => {
            const value = ticket.additionalFields[field.key];

            return (
              <Field
                key={field.key}
                label={field.label}
                value={formatCustomFieldValue(field, value)}
              />
            );
          })}
        </>
      )}
    </Stack>
  </Paper>
);

export default function ReviewStep({ formData, ticketTypes, customFields }) {
  const { t } = useTranslation();

  const registrationCustomFields = customFields.filter(
    (f) => f.appliedTo === "registrations"
  );
  const ticketCustomFields = customFields.filter(
    (f) => f.appliedTo === "ticket"
  );

  const getTicketTypeName = (ticketTypeId) => {
    const ticketType = ticketTypes.find((t) => String(t.id) === ticketTypeId);
    return `${ticketType?.name}${
      ticketType?.price ? ` (${ticketType.price})` : ""
    }${ticketType?.description ? ` - ${ticketType.description}` : ""}`;
  };

  const formatCustomFieldValue = (field, value) => {
    if (value == null || value === "") return "N/A";

    if (field.type === "boolean") {
      return value ? t("yes") : t("no");
    }

    if (field.type === "date") {
      return dayjs(value).format("YYYY-MM-DD");
    }

    if (field.type === "multiselect" && Array.isArray(value)) {
      return value.join(", ");
    }

    return String(value);
  };

  return (
    <Stack gap="md">
      <Title order={4}>{t("review_registration")}</Title>
      <Text size="sm" c="dimmed">
        {t("review_registration_description")}
      </Text>

      <Divider />

      <Paper p="md" withBorder>
        <Title order={5} mb="sm">
          {t("registration_information")}
        </Title>

        <Stack gap="xs">
          <RegistrationFields
            formData={formData}
            registrationCustomFields={registrationCustomFields}
            formatCustomFieldValue={formatCustomFieldValue}
            t={t}
          />
        </Stack>
      </Paper>

      {formData.tickets && formData.tickets.length > 0 && (
        <Paper p="md" withBorder>
          <Title order={5} mb="sm">
            {t("tickets")} ({formData.tickets.length})
          </Title>

          <Stack gap="md">
            {formData.tickets.map((ticket, index) => (
              <TicketCard
                key={index}
                ticket={ticket}
                index={index}
                ticketCustomFields={ticketCustomFields}
                getTicketTypeName={getTicketTypeName}
                formatCustomFieldValue={formatCustomFieldValue}
                t={t}
              />
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
